import { defineComponent, PropType, reactive, watch } from '@vue/composition-api';
import { ConnectionStatus } from "@/views/connections/connections-types";
import { TeamtailorApiServiceInfo, enableTeamtailorApi, updateTeamtailorApi, disableTeamtailorApi } from '@/functions/services-functions';
import { confirmDialogue, generalError, inputDialogue } from '@/functions/system-modal-functions';
import { i18n } from "@/utilities/vue-i18n";
import { ModalIconType } from '../system-modals/system-modals';

type SettingsType = {
  connection: TeamtailorApiServiceInfo;
  loading: boolean;
  company: number;
};

export default defineComponent({
  props: {
    connection: {
      required: true,
      type: Object as PropType<TeamtailorApiServiceInfo>,
    },
    companyId: {
      required: false,
      type: Number,
    },
    allowDelete: {
      required: false,
      type: Boolean,
      default: false,
    },
    isAdmin: {
      required: false,
      type: Boolean,
      default: false,
    },
    loading: {
      required: false,
      type: Boolean,
      default: false,
    }
  },
  setup(props, context) {
    const settings = reactive<SettingsType>({
      connection: { ...props.connection },
      loading: props.loading,
      company: props.companyId ?? +context.root.$route.params.company,
    });

    watch(() => props.connection, (value) => settings.connection = { ...value }, { deep: true });
    watch(() => props.companyId, (value) => settings.company = value ?? +context.root.$route.params.company);
    watch(() => props.loading, (value) => settings.loading = value ?? settings.loading);

    const promptApiKey = async () => {
      return await inputDialogue({
        title: 'Teamtailor API',
        message: i18n.t('components.teamtailor-api-connection.enter-api-key'),
        validation: (i) => i?.trim().length > 0,
      });
    };

    const enable = async () => {
      if (settings.loading) {
        return;
      }

      settings.loading = true;

      const dialogue = await promptApiKey();

      if (dialogue.confirmed) {
        const service = await enableTeamtailorApi(settings.company, dialogue.input);

        if (service === null) {
          generalError(false);
        } else {
          settings.connection = service;
          context.emit('updated', settings.connection);
        }
      }

      settings.loading = false;
    };

    const update = async () => {
      if (settings.loading) {
        return;
      }

      settings.loading = true;

      const dialogue = await promptApiKey();

      if (dialogue.confirmed) {
        const service = await updateTeamtailorApi(settings.company, dialogue.input);

        if (service === null) {
          generalError(false);
        } else {
          settings.connection = service;
          context.emit('updated', settings.connection);
        }
      }

      settings.loading = false;
    };

    const disable = async () => {
      if (!props.allowDelete || settings.loading) {
        return;
      }

      settings.loading = true;

      const confirmed = await confirmDialogue(
        'Teamtailor API',
        i18n.t('components.teamtailor-api-connection.disable-integration'),
        ModalIconType.warning
      );

      if (confirmed) {
        if (await disableTeamtailorApi(settings.company)) {
          context.emit('updated', null);
        } else {
          await generalError(false);
        }
      }

      settings.loading = false;
    };

    return {
      settings,
      ConnectionStatus,
      enable,
      update,
      disable,
    }
  },
})
